import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";
import { RequestError } from "@/app/queries";

/** Автопроверка (SP3): прогоны, условия отправки, сдача, эталон.
 *  Пока прогон в очереди или идёт — опрос раз в секунду; SSE-клиента в SPA пока нет. */
export type Run = components["schemas"]["Run"];
export type GradeResult = components["schemas"]["GradeResult"];
export type GradeTask = components["schemas"]["GradeTask"];
export type Eligibility = components["schemas"]["Eligibility"];
export type Condition = components["schemas"]["Condition"];

function fail(status: number, error: unknown): never {
  const e = error as { error?: string; message?: string } | undefined;
  throw new RequestError(status, e?.error ?? "unknown", e?.message ?? "Не удалось выполнить запрос.");
}

export const gradeKeys = {
  runs: (assignmentId: string) => ["runs", assignmentId] as const,
  run: (runId: string) => ["run", runId] as const,
  eligibility: (assignmentId: string) => ["eligibility", assignmentId] as const,
  referenceRuns: (assignmentId: string) => ["admin", "reference-runs", assignmentId] as const,
};

export const POLL_MS = 1000;
const noRetry4xx = (n: number, err: unknown) => !(err instanceof RequestError && err.status >= 400 && err.status < 500) && n < 2;

/** Прогон ещё не завершён — нужно опрашивать. */
export const isActive = (r: Pick<Run, "status"> | undefined) => r?.status === "queued" || r?.status === "running";

/** История прогонов студента, новые сверху. Опрос, пока верхний прогон не завершён. */
export function useRuns(assignmentId: string) {
  return useQuery({
    queryKey: gradeKeys.runs(assignmentId),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/assignments/{assignmentId}/runs", { params: { path: { assignmentId } } });
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
    refetchInterval: (q) => (isActive(q.state.data?.[0]) ? POLL_MS : false),
  });
}

/** Один прогон с результатом. Опрос, пока не завершён. */
export function useRun(runId: string | undefined) {
  return useQuery({
    queryKey: gradeKeys.run(runId ?? ""),
    enabled: !!runId,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/runs/{runId}", { params: { path: { runId: runId! } } });
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
    refetchInterval: (q) => (isActive(q.state.data) ? POLL_MS : false),
  });
}

/** Условия отправки ментору — причина блокировки всегда отсюда (правило 3). */
export function useEligibility(assignmentId: string) {
  return useQuery({
    queryKey: gradeKeys.eligibility(assignmentId),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/assignments/{assignmentId}/eligibility", { params: { path: { assignmentId } } });
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
    refetchInterval: (q) => (q.state.data?.conditions.some((c) => c.key === "run" && c.text.startsWith("Прогон ещё идёт")) ? POLL_MS : false),
  });
}

function invalidateAssignment(qc: ReturnType<typeof useQueryClient>, assignmentId: string) {
  void qc.invalidateQueries({ queryKey: gradeKeys.runs(assignmentId) });
  void qc.invalidateQueries({ queryKey: gradeKeys.eligibility(assignmentId) });
}

/** «проверить на сервере»: снимок черновика → прогон в очереди. */
export function useStartRun(assignmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error, response } = await api.POST("/assignments/{assignmentId}/runs", { params: { path: { assignmentId } } });
      if (!data) fail(response.status, error);
      return data;
    },
    onSuccess: (run) => {
      qc.setQueryData(gradeKeys.run(run.id), run);
      invalidateAssignment(qc, assignmentId);
    },
  });
}

/** «отправить на проверку»: 409 несёт причину в message. */
export function useSubmitRun(assignmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (runId?: string) => {
      const { data, error, response } = await api.POST("/assignments/{assignmentId}/submit", { params: { path: { assignmentId } }, body: runId ? { run_id: runId } : {} });
      if (!data) fail(response.status, error);
      return data;
    },
    onSettled: () => invalidateAssignment(qc, assignmentId),
  });
}

/** Прогоны эталона (A1), новые сверху. */
export function useReferenceRuns(assignmentId: string | undefined) {
  return useQuery({
    queryKey: gradeKeys.referenceRuns(assignmentId ?? ""),
    enabled: !!assignmentId,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/admin/assignments/{assignmentId}/reference-runs", { params: { path: { assignmentId: assignmentId! } } });
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
    refetchInterval: (q) => (isActive(q.state.data?.[0]) ? POLL_MS : false),
  });
}

/** «прогнать эталон» (A1). */
export function useStartReferenceRun(assignmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error, response } = await api.POST("/admin/assignments/{assignmentId}/reference-runs", { params: { path: { assignmentId } } });
      if (!data) fail(response.status, error);
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: gradeKeys.referenceRuns(assignmentId) }),
  });
}
